import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardList, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import useDialog from '../hooks/useDialog';
import Select from './Select';
import { intakeAPI } from '../services/api';
import { BAND_LABEL, getAgeBandKey } from '../utils/vitals';

/**
 * The first-visit medical history the doctor takes before writing a patient's first prescription.
 * Asked once per patient and kept for the life of the file.
 *
 * <p>Blocking by design (owner's decision, 2026-09-23): the prescription workspace does not open
 * until this is saved. The only way past it is Back to Queue, which leaves the prescription
 * entirely, so a doctor who opened the wrong patient is not trapped.
 *
 * <p>Which questions appear is the server's call, from the patient's age band and gender. The band
 * is shown in the header so the doctor can see why a child's form differs from an adult's.
 */

const SECTION_LABEL = {
  FAMILY_HISTORY: 'Family history',
  PERSONAL_HISTORY: 'Personal history',
  BIRTH_HISTORY: 'Birth and early history',
};
const SECTION_ORDER = ['FAMILY_HISTORY', 'PERSONAL_HISTORY', 'BIRTH_HISTORY'];

const INPUT =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500';

/** One question: the Urdu the doctor reads, the English label, and the answer control. */
const QuestionRow = ({ question, value, onChange, showError }) => {
  const answer = value?.answer || '';
  const detail = value?.detail || '';
  const missing = showError && question.isRequired && !answer;
  const set = (patch) => onChange({ answer, detail, ...patch });

  const choices = (question.choices || '')
    .split('\n')
    .map((c) => c.trim())
    .filter(Boolean);

  return (
    <div
      className={`p-3 rounded-lg border ${
        missing ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="min-w-0">
          {/* The question as the doctor reads it aloud. App.css forces .urdu-text right-aligned,
              which is correct here: this is a full RTL sentence, not a centred heading. */}
          <p className="urdu-text text-base text-gray-900" dir="rtl" style={{ lineHeight: 2.2 }}>
            {question.textUrdu}
          </p>
          <p className="text-xs text-gray-500">
            {question.labelEnglish}
            {question.isRequired ? <span className="text-red-500"> *</span> : null}
          </p>
        </div>

        <div className="flex-shrink-0 w-full sm:w-64">
          {question.answerType === 'YES_NO' && (
            <div className="flex gap-2">
              {[
                { v: 'YES', label: 'Yes' },
                { v: 'NO', label: 'No' },
                { v: 'NOT_KNOWN', label: 'Not known' },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set({ answer: answer === o.v ? '' : o.v })}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    answer === o.v
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}

          {question.answerType === 'CHOICE' && (
            <Select
              value={answer}
              onChange={(v) => set({ answer: v })}
              options={choices.map((c) => ({ value: c, label: c }))}
              placeholder="Select"
              buttonClassName="px-3 py-2 rounded-lg"
            />
          )}

          {question.answerType === 'TEXT' && (
            <input
              type="text"
              value={answer}
              onChange={(e) => set({ answer: e.target.value })}
              maxLength={200}
              className={INPUT}
            />
          )}

          {question.answerType === 'NUMBER' && (
            <input
              type="number"
              value={answer}
              onChange={(e) => set({ answer: e.target.value })}
              className={INPUT}
            />
          )}

          {question.answerType === 'DATE' && (
            <input
              type="date"
              value={answer}
              onChange={(e) => set({ answer: e.target.value })}
              className={INPUT}
            />
          )}
        </div>
      </div>

      {/* "Yes, and who / what" — only when the catalogue asks for it and the answer is Yes. */}
      {question.requiresDetail && answer === 'YES' && (
        <input
          type="text"
          value={detail}
          onChange={(e) => set({ detail: e.target.value })}
          maxLength={200}
          placeholder="Who, or what?"
          className={`${INPUT} mt-2`}
        />
      )}
    </div>
  );
};

const PatientIntakeModal = ({ patient, onSaved, onLeave }) => {
  const [questions, setQuestions] = useState(null); // null = loading
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const bodyRef = useRef(null);
  // No dismiss handler: this dialog is not escapable, by design. useDialog still traps focus.
  const dialogRef = useDialog(() => {}, { confirmDiscard: false });

  const band = getAgeBandKey(patient?.dateOfBirth);

  useEffect(() => {
    let cancelled = false;
    if (!patient?.id) return undefined;
    setQuestions(null);
    setError(null);
    intakeAPI
      .forPatient(patient.id)
      .then((data) => {
        if (cancelled) return;
        setQuestions(Array.isArray(data?.questions) ? data.questions : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || 'Could not load the intake form');
        setQuestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [patient?.id]);

  const sections = useMemo(() => {
    const grouped = new Map();
    (questions || []).forEach((q) => {
      const key = q.section || 'FAMILY_HISTORY';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(q);
    });
    return SECTION_ORDER.filter((k) => grouped.has(k)).map((k) => [k, grouped.get(k)]);
  }, [questions]);

  const missingCount = (questions || []).filter(
    (q) => q.isRequired && !(answers[q.id]?.answer || '')
  ).length;

  const handleSave = async () => {
    if (missingCount > 0) {
      setShowErrors(true);
      setError(`${missingCount} question${missingCount === 1 ? '' : 's'} still need an answer.`);
      const firstBad = bodyRef.current?.querySelector('.border-red-300');
      if (firstBad) firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await intakeAPI.save(patient.id, {
        answers: (questions || [])
          .map((q) => ({
            questionId: q.id,
            answer: answers[q.id]?.answer || '',
            detail: answers[q.id]?.detail || '',
          }))
          .filter((a) => a.answer),
      });
      onSaved();
    } catch (e) {
      setError(e.message || 'Could not save the intake form');
      setSaving(false);
    }
  };

  const patientName = `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim();

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="intake-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
      >
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 id="intake-title" className="text-xl font-bold text-white">
                Medical History
              </h2>
              <p className="text-emerald-100 text-sm truncate">
                {patientName}
                {patient?.mrn ? ` · ${patient.mrn}` : ''} · {BAND_LABEL[band] || 'adult'} questions
              </p>
            </div>
          </div>
        </div>

        <div ref={bodyRef} className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">
          <p className="text-sm text-gray-600">
            Taken once, and kept on this patient's file. The questions follow the patient's age, so
            a child is asked about birth and vaccination where an adult is asked about smoking.
          </p>

          {questions === null ? (
            <div className="flex justify-center py-16">
              <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500">
                No intake questions are set up for this patient's age group.
              </p>
              <p className="text-gray-400 text-sm mt-1">
                An admin can add them in Settings, Intake Questions.
              </p>
            </div>
          ) : (
            sections.map(([key, rows]) => (
              <div key={key}>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  {SECTION_LABEL[key] || key}
                </h3>
                <div className="space-y-2">
                  {rows.map((q) => (
                    <QuestionRow
                      key={q.id}
                      question={q}
                      value={answers[q.id]}
                      showError={showErrors}
                      onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-4 space-y-3">
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            {/* The only way out without answering. It leaves the prescription rather than
                dismissing the form, so the block cannot simply be clicked away. */}
            <button
              type="button"
              onClick={onLeave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to queue</span>
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || questions === null}
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors disabled:opacity-60"
            >
              {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
              <span>{saving ? 'Saving...' : 'Save and continue'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientIntakeModal;
